import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Calendar } from 'lucide-react';
import PropTypes from 'prop-types';
import { clsx } from 'clsx';
import { BLOG_TAGS, formatBlogDate } from '../../data/blogPosts';

/**
 * Blog Card component for displaying blog post previews
 */
const BlogCard = ({ post, featured = false, className = '' }) => {
  return (
    <article
      className={clsx(
        'bg-white rounded-2xl overflow-hidden border border-neutral-100',
        'hover:shadow-lg transition-all duration-300 group',
        featured ? 'md:col-span-2 md:grid md:grid-cols-2' : 'flex flex-col',
        className
      )}
    >
      {/* Image */}
      <Link to={`/blog/${post.slug}`} className="block overflow-hidden">
        <div className={clsx('overflow-hidden', featured ? 'h-full min-h-[250px]' : 'h-48')}>
          <img
            src={post.image || '/images/services/siemens-mri.jpg'}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
      </Link>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {post.tags.map((tag) => {
            const tagInfo = BLOG_TAGS[tag];
            return tagInfo ? (
              <Link
                key={tag}
                to={`/blog/tags/${tag}`}
                className={clsx('text-xs font-medium px-2.5 py-1 rounded-full', tagInfo.color)}
              >
                {tagInfo.label}
              </Link>
            ) : null;
          })}
        </div>

        {/* Title */}
        <h3 className={clsx('font-bold text-neutral-900 mb-2 group-hover:text-primary-600 transition-colors', featured ? 'text-xl md:text-2xl' : 'text-lg')}>
          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
        </h3>

        {/* Description */}
        <p className={clsx('text-neutral-600 leading-relaxed mb-4 flex-1', featured ? 'text-base' : 'text-sm line-clamp-3')}>
          {post.description}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
          <div className="flex items-center gap-4 text-sm text-neutral-500">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>{post.readingTime} min read</span>
            </div>
          </div>

          <Link
            to={`/blog/${post.slug}`}
            className="inline-flex items-center text-primary-600 text-sm font-medium gap-1 hover:gap-2 transition-all"
          >
            Read
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Author */}
        {featured && post.author && (
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-neutral-100">
            {post.author.avatar ? (
              <img src={post.author.avatar} alt="" className="w-8 h-8 rounded-full object-cover" loading="lazy" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold text-xs">{post.author.name.charAt(0)}</span>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-neutral-900">{post.author.name}</p>
              <p className="text-xs text-neutral-500">{post.author.title}</p>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

BlogCard.propTypes = {
  post: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    readingTime: PropTypes.number.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    image: PropTypes.string,
    author: PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      avatar: PropTypes.string,
    }),
  }).isRequired,
  featured: PropTypes.bool,
  className: PropTypes.string,
};

export default BlogCard;
